import {
  Flex,
  Card,
  KpiSkeleton,
  ProgressBar,
  CurrencyValue,
  PercentageValue,
  Box,
  Heading
} from '@blockanalitica/ui';
import { useMemo } from 'react';
import { useApi } from '@/api';
import { useTheme } from 'styled-components';
import { ReactNode } from 'react';

type Emode = {
  category: string;
  label: string;
  ltv: string;
  liquidation_bonus: string;
  liquidation_threshold: string;
};

type ParamsResponse = {
  ltv: string;
  liquidation_threshold: string;
  liquidation_bonus: string;
  supply_cap: string;
  supply_cap_usd: string;
  borrow_cap: string;
  borrow_cap_usd: string;
  reserve_factor: string;
  emodes: Emode[];
};

export default function Params({
  marketAddress,
  supply,
  borrow,
  ...rest
}: {
  marketAddress: string;
  supply: string;
  borrow: string;
}) {
  const theme = useTheme();
  const [data, error] = useApi<ParamsResponse>({
    url: `/markets/${marketAddress}/params/`
  });

  const supplyData: {
    percent: number;
    additionalValue: ReactNode;
    color: string;
  } = useMemo(() => {
    if (!data) {
      return { percent: 0, additionalValue: null, color: theme.colors.primary };
    }
    const percent = Number(supply) / Number(data.supply_cap);
    const additionalValue = (
      <Flex gap="4xs">
        <CurrencyValue value={supply} />
        /
        <CurrencyValue value={data.supply_cap} />
      </Flex>
    );
    return {
      percent,
      additionalValue,
      color: theme.colors.primary
    };
  }, [supply, data, theme]);

  const borrowData: {
    percent: number;
    additionalValue: ReactNode;
    color: string;
  } = useMemo(() => {
    if (!data) {
      return {
        percent: 0,
        additionalValue: null,
        color: theme.colors.secondary
      };
    }
    const percent = Number(borrow) / Number(data.borrow_cap);
    const additionalValue = (
      <Flex gap="4xs">
        <CurrencyValue value={borrow} />
        /
        <CurrencyValue value={data.borrow_cap} />
      </Flex>
    );
    return {
      percent,
      additionalValue,
      color: theme.colors.secondary
    };
  }, [borrow, data, theme]);

  if (error) {
    throw error;
  }

  if (!data) {
    return <KpiSkeleton />;
  }

  return (
    <Card {...rest}>
      <Heading size="4" paddingBottom="xs">
        Parameters
      </Heading>
      <Flex gap="3xs" flexDirection="column">
        <Flex gap="s" flexDirection="column" marginBottom="xs">
          <ProgressBar
            text="Supply cap"
            percent={supplyData.percent}
            additionalValue={supplyData.additionalValue}
            color={supplyData.color}
          />

          <ProgressBar
            text="Borrow cap"
            percent={borrowData.percent}
            additionalValue={borrowData.additionalValue}
            color={borrowData.color}
          />
        </Flex>

        <Flex justifyContent="space-between">
          <Box color="muted">LTV</Box>
          <PercentageValue value={data.ltv} />
        </Flex>
        <Flex justifyContent="space-between">
          <Box color="muted">Liquidation Threshold</Box>
          <PercentageValue value={data.liquidation_threshold} />
        </Flex>

        <Flex justifyContent="space-between">
          <Box color="muted">Liquidation Bonus</Box>
          <PercentageValue value={data.liquidation_bonus} />
        </Flex>

        <Flex justifyContent="space-between">
          <Box color="muted">Reserve Factor</Box>
          <PercentageValue value={data.reserve_factor} />
        </Flex>

        {data.emodes && data.emodes.length > 0 ? (
          <Flex flexDirection="column" paddingTop="2xs">
            <Heading size="5">Emodes</Heading>

            {data.emodes.map((emode) => (
              <Flex
                flexDirection="column"
                gap="3xs"
                paddingTop="xs"
                key={emode.category}>
                <Flex gap="2xs">
                  {emode.label}
                  <Box color="muted">(Category: {emode.category})</Box>
                </Flex>
                <Flex justifyContent="space-between">
                  <Box color="muted">LTV</Box>
                  <PercentageValue value={emode.ltv} />
                </Flex>
                <Flex justifyContent="space-between">
                  <Box color="muted">Liquidation Threshold</Box>
                  <PercentageValue value={emode.liquidation_threshold} />
                </Flex>
                <Flex justifyContent="space-between">
                  <Box color="muted">Liquidation Bonus</Box>
                  <PercentageValue value={emode.liquidation_bonus} />
                </Flex>
              </Flex>
            ))}
          </Flex>
        ) : null}
      </Flex>
    </Card>
  );
}
